import type { Project } from '../../features/projects/models/project.model';
import type { Task, TaskNote } from '../../features/tasks/models/task.model';
import type { User } from '../../features/users/models/user.model';

/**
 * Données de démonstration (mode `environment.mock`).
 * Elles servent UNIQUEMENT à tester l'interface sans backend — elles sont
 * clairement signalées par le badge « Mode démo » dans l'application.
 */

/** Login de l'utilisateur de démonstration. */
export const MOCK_LOGIN = 'admin';

export function daysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export const mockProjects: Project[] = [
  {
    id: 'p-1',
    name: 'Refonte du site web',
    code: 'WEB',
    description: 'Refonte complète du site public Centoria : design, contenu et CMS.',
    status: 'active',
    createdAt: '2026-08-12T10:00:00Z',
    updatedAt: '2026-10-18T14:30:00Z',
  },
  {
    id: 'p-1-1',
    name: 'Refonte — Design system',
    code: 'WEB-DS',
    description: 'Identité visuelle et composants du nouveau site.',
    status: 'active',
    parentId: 'p-1',
    createdAt: '2026-08-15T09:00:00Z',
    updatedAt: '2026-10-12T16:00:00Z',
  },
  {
    id: 'p-1-2',
    name: 'Refonte — Migration CMS',
    code: 'WEB-CMS',
    description: 'Migration des contenus vers le nouveau CMS.',
    status: 'active',
    parentId: 'p-1',
    createdAt: '2026-09-01T09:00:00Z',
    updatedAt: '2026-10-05T11:20:00Z',
  },
  {
    id: 'p-2',
    name: 'Application mobile',
    code: 'MOB',
    description: 'Application mobile de gestion de projets (iOS / Android).',
    status: 'active',
    createdAt: '2026-07-02T08:00:00Z',
    updatedAt: '2026-10-17T09:45:00Z',
  },
  {
    id: 'p-3',
    name: 'Migration ERP',
    code: 'ERP',
    description: 'Migration des données vers la nouvelle version de l’ERP.',
    status: 'closed',
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-06-30T17:00:00Z',
  },
  {
    id: 'p-4',
    name: 'Site vitrine',
    code: 'VITRINE',
    description: 'Ancien site vitrine de l’entreprise.',
    status: 'closed',
    createdAt: '2025-09-01T10:00:00Z',
    updatedAt: '2026-03-12T12:00:00Z',
  },
];

export const mockTasks: Task[] = [
  {
    id: 't-1',
    name: 'Sélecteur de membres par projet',
    description:
      'N’afficher dans la liste des assignés que les membres qui participent réellement au projet.',
    status: 'Backlog',
    priority: 'Medium',
    assignedTo: { name: 'Sara Martin' },
    dueDate: daysFromNow(6),
    projectId: 'p-1',
    notes: [
      { id: 'n-1', description: 'À cadrer avec le service métier.', createdAt: '2026-10-15' },
    ],
    createdAt: '2026-10-10T10:00:00Z',
    updatedAt: '2026-10-15T14:00:00Z',
  },
  {
    id: 't-2',
    name: 'Accès rapide aux tableaux',
    description: 'Pouvoir atteindre un tableau sans passer par le détail du projet.',
    status: 'Backlog',
    priority: 'Low',
    assignedTo: { name: 'Omar Benali' },
    dueDate: daysFromNow(12),
    projectId: 'p-1',
    notes: [],
    createdAt: '2026-10-08T10:00:00Z',
    updatedAt: '2026-10-09T10:00:00Z',
  },
  {
    id: 't-3',
    name: 'Messages toast sur les échecs API',
    description: 'Afficher un toast sur tous les appels API qui échouent.',
    status: 'To Do',
    priority: 'Medium',
    assignedTo: { name: 'admin' },
    dueDate: daysFromNow(4),
    projectId: 'p-2',
    notes: [],
    createdAt: '2026-10-11T10:00:00Z',
    updatedAt: '2026-10-16T10:00:00Z',
  },
  {
    id: 't-4',
    name: 'Zone de description des tâches',
    description: 'Augmenter le nombre de lignes par défaut du champ description.',
    status: 'To Do',
    priority: 'Low',
    assignedTo: { name: 'Sara Martin' },
    dueDate: daysFromNow(9),
    projectId: 'p-1-1',
    notes: [],
    createdAt: '2026-10-12T10:00:00Z',
    updatedAt: '2026-10-13T10:00:00Z',
  },
  {
    id: 't-5',
    name: 'Notification de perte réseau',
    description: 'Prévenir l’utilisateur quand la connexion réseau est perdue.',
    status: 'In Progress',
    priority: 'High',
    assignedTo: { name: 'admin' },
    dueDate: daysFromNow(-1),
    projectId: 'p-2',
    notes: [
      { id: 'n-2', description: 'Maquette validée.', createdAt: '2026-10-14' },
    ],
    createdAt: '2026-10-02T10:00:00Z',
    updatedAt: '2026-10-18T16:00:00Z',
  },
  {
    id: 't-6',
    name: 'Ajouter une liste au tableau',
    description: 'Permettre d’ajouter / renommer / supprimer les colonnes du tableau.',
    status: 'In Progress',
    priority: 'Medium',
    assignedTo: { name: 'Omar Benali' },
    dueDate: daysFromNow(2),
    projectId: 'p-1',
    notes: [],
    createdAt: '2026-10-05T10:00:00Z',
    updatedAt: '2026-10-17T10:00:00Z',
  },
  {
    id: 't-7',
    name: 'Sélecteur multi-organisations',
    description: 'Page de sélection quand l’utilisateur appartient à plusieurs organisations.',
    status: 'In Review',
    priority: 'High',
    assignedTo: { name: 'Sara Martin' },
    dueDate: daysFromNow(5),
    projectId: 'p-2',
    notes: [],
    createdAt: '2026-09-20T10:00:00Z',
    updatedAt: '2026-10-16T10:00:00Z',
  },
  {
    id: 't-8',
    name: 'Menu déroulant de priorité',
    description: 'Implémenter le sélecteur de priorité dans la modale de tâche.',
    status: 'Done',
    priority: 'Medium',
    assignedTo: { name: 'admin' },
    dueDate: daysFromNow(-3),
    projectId: 'p-1-1',
    notes: [],
    createdAt: '2026-09-10T10:00:00Z',
    updatedAt: '2026-10-14T10:00:00Z',
  },
  {
    id: 't-9',
    name: 'État de chargement de l’accueil',
    description: 'Ajouter un état de chargement à la page d’accueil.',
    status: 'Done',
    priority: 'Low',
    assignedTo: { name: 'Sara Martin' },
    dueDate: daysFromNow(-5),
    projectId: 'p-2',
    notes: [],
    createdAt: '2026-09-05T10:00:00Z',
    updatedAt: '2026-10-11T10:00:00Z',
  },
  {
    id: 't-10',
    name: 'Invitation par e-mail',
    description: 'Accepter / refuser une invitation depuis la page de sélection d’organisation.',
    status: 'To Do',
    priority: 'High',
    assignedTo: { name: 'Omar Benali' },
    dueDate: daysFromNow(8),
    projectId: 'p-1-2',
    notes: [],
    createdAt: '2026-10-13T10:00:00Z',
    updatedAt: '2026-10-13T10:00:00Z',
  },
];

// Ajoute une date de début estimée aux tâches pour alimenter le diagramme de Gantt.
const MOCK_TASK_DURATION_DAYS = 5;
for (const task of mockTasks) {
  if (task.dueDate && !task.startDate) {
    const end = new Date(task.dueDate);
    task.startDate = new Date(end.getTime() - MOCK_TASK_DURATION_DAYS * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
  }
}

export const mockUsers: User[] = [
  {
    id: 'u-1',
    login: 'admin',
    name: 'Yassine Ettai',
    contact: { name: 'Yassine Ettai', email: 'yassine.et-tai@centoria.fr' },
    roles: [{ label: 'Admin' }],
    createdAt: '2025-02-01',
  },
  {
    id: 'u-2',
    login: 'sara.martin',
    name: 'Sara Martin',
    contact: { name: 'Sara Martin', email: 'sara.martin@centoria.fr' },
    roles: [{ label: 'Chef de projet' }],
    createdAt: '2025-04-15',
  },
  {
    id: 'u-3',
    login: 'omar.benali',
    name: 'Omar Benali',
    contact: { name: 'Omar Benali', email: 'omar.benali@centoria.fr' },
    roles: [{ label: 'Développeur' }],
    createdAt: '2025-06-20',
  },
  {
    id: 'u-4',
    login: 'leila.amrani',
    name: 'Leila Amrani',
    contact: { name: 'Leila Amrani', email: 'leila.amrani@centoria.fr' },
    roles: [{ label: 'Designer' }],
    createdAt: '2025-09-01',
  },
];
